import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Alert,
  Typography,
} from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LockIcon from "@mui/icons-material/Lock";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useGroupRequestMutation,
  useActivateGroupMutation,
  useGroupStartMutation,
  useRequestToJoinMutation,
  useWithdrawJoinRequestMutation,
} from "../../../../Slices/circleApiSlice";
import { useGetStatusQuery } from "../../../../Slices/usersApiSlice";
import GroupAgreementModal from "components/Circle/shared/Agreement";

const buttonStyles = {
  base: {
    height: 44,
    borderRadius: "10px",
  },
  wide: {
    width: "100%",
    maxWidth: 360,
    height: 52,
    borderRadius: "12px",
    fontSize: "1rem",
    fontWeight: 600,
  },
  disabled: {
    backgroundColor: "text.secondary",
    "&.Mui-disabled": {
      backgroundColor: "#A1A1A1",
      color: "#ffffff",
    },
  },
};

const withdrawLinkStyles = {
  mt: 1,
  color: "error.main",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": { color: "error.secondary" },
};

// Sub-components for cleaner render
const WithdrawLink = ({ onClick, isLoading }) => (
  <Link
    component="button"
    variant="body2"
    onClick={onClick}
    sx={{
      ...withdrawLinkStyles,
      cursor: isLoading ? "not-allowed" : "pointer",
    }}
  >
    {isLoading ? "Withdrawing..." : "Withdraw Request"}
  </Link>
);

const RequestToJoinButton = ({
  onClick,
  isLoading,
  showWithdraw,
  onWithdraw,
  isWithdrawing,
  isUserActive = true,
}) => (
  <Box display="flex" flexDirection="column" alignItems="center" width="100%">
    <Button
      disabled={!isUserActive || isLoading || showWithdraw}
      startIcon={
        !isUserActive ? (
          <LockIcon />
        ) : isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : showWithdraw ? (
          <HourglassEmptyIcon />
        ) : (
          <PersonAddIcon />
        )
      }
      sx={{
        ...buttonStyles.wide,
        ...(showWithdraw || !isUserActive ? buttonStyles.disabled : {}),
      }}
      variant="contained"
      color="primary"
      onClick={onClick}
    >
      {!isUserActive
        ? "Complete Onboarding"
        : isLoading
          ? "Sending Request..."
          : showWithdraw
            ? "Awaiting Approval"
            : "Request to Join"}
    </Button>
    {showWithdraw && (
      <WithdrawLink onClick={onWithdraw} isLoading={isWithdrawing} />
    )}
  </Box>
);

const AwaitingApprovalButton = ({ onWithdraw, isWithdrawing }) => (
  <Box display="flex" flexDirection="column" alignItems="center" width="100%">
    <Button
      disabled
      startIcon={<HourglassEmptyIcon />}
      sx={{ ...buttonStyles.wide, ...buttonStyles.disabled }}
      variant="contained"
    >
      Awaiting Admin Approval
    </Button>
    <WithdrawLink onClick={onWithdraw} isLoading={isWithdrawing} />
  </Box>
);

const InvitationButtons = ({ onAccept, onDecline }) => (
  <Box display="flex" flexDirection="column" gap={1.5} width="100%">
    <Button
      onClick={onAccept}
      sx={{ ...buttonStyles.wide }}
      variant="contained"
      color="primary"
    >
      Accept Invitation
    </Button>
    <Button
      onClick={onDecline}
      sx={{
        ...buttonStyles.wide,
        bgcolor: "transparent",
        color: "error.main",
        border: "1px solid",
        borderColor: "error.light",
        "&:hover": { bgcolor: "error.50", borderColor: "error.main" },
      }}
      variant="outlined"
    >
      Decline
    </Button>
  </Box>
);

function PendingAction({ group, status, canAddSelf }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  // Fetch user status to check if onboarding is complete
  const { data: userStatusData } = useGetStatusQuery();
  const PARTICIPATABLE_STATUSES = [
    "active",
    "complete",
    "pending",
    "verifying",
    "verification_failed",
  ];
  const isUserActive = PARTICIPATABLE_STATUSES.includes(
    userStatusData?.userStatus?.toLowerCase() ?? "",
  );

  // Mutations
  const [groupRequest] = useGroupRequestMutation();
  const [activateGroup] = useActivateGroupMutation();
  const [groupStart] = useGroupStartMutation();
  const [requestToJoin, { isLoading: isRequesting }] =
    useRequestToJoinMutation();
  const [withdrawJoinRequest, { isLoading: isWithdrawing }] =
    useWithdrawJoinRequestMutation();

  // Local state
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [canStart, setCanStart] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestWithdrawn, setRequestWithdrawn] = useState(false);

  // Check if admin can start the circle
  useEffect(() => {
    if (!group?.id) return;

    const checkCanStart = async () => {
      try {
        const res = await groupStart(group.id).unwrap();
        setCanStart(res);
      } catch (err) {
        console.error("Error checking start status:", err);
      }
    };
    checkCanStart();
  }, [group?.id, groupStart]);

  if (!user) return null;

  // Derived state
  const isPublicCircle = !group.isPrivate;
  const canRequestToJoin = !status && isPublicCircle && !group.isAdmin;
  const showRequestAfterWithdraw =
    requestWithdrawn && isPublicCircle && !group.isAdmin;
  const isAwaitingApproval = status === "pending" && canAddSelf === false;
  const wasInvited = status === "pending" && canAddSelf === true;

  // Handlers
  const handleRequestToJoin = async () => {
    try {
      await requestToJoin({ circleId: group.id }).unwrap();
      setRequestSent(true);
      setRequestWithdrawn(false);
    } catch (err) {
      console.error("Error requesting to join:", err);
    }
  };

  const handleWithdrawRequest = async () => {
    try {
      await withdrawJoinRequest({ circleId: group.id }).unwrap();
      setRequestSent(false);
      setRequestWithdrawn(true);
    } catch (err) {
      console.error("Error withdrawing request:", err);
    }
  };

  const handleDecision = async (decision) => {
    try {
      await groupRequest({ groupId: group.id, decision }).unwrap();
      navigate("/");
    } catch (err) {
      console.error("Error sending decision:", err);
    }
  };

  const handleStartCircle = async () => {
    try {
      await activateGroup({ groupId: group.id }).unwrap();
      window.location.reload();
    } catch (err) {
      console.error("Error starting circle:", err);
    }
  };

  // Render logic
  const renderContent = () => {
    // Admin view
    if (group.isAdmin) {
      return (
        <Box>
          <Button
            disabled={!canStart}
            sx={{ ...buttonStyles.wide, backgroundColor: "primary.main" }}
            variant="contained"
            onClick={handleStartCircle}
          >
            Start Circle
          </Button>
          {/* A disabled button with no reason reads as broken — say why. */}
          {!canStart && (
            <Typography
              sx={{
                mt: 1,
                fontSize: "0.78rem",
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              You can start once at least {group.minMembers} members have joined
              and accepted.
            </Typography>
          )}
        </Box>
      );
    }

    // User just withdrew their request
    if (showRequestAfterWithdraw) {
      return (
        <RequestToJoinButton
          onClick={handleRequestToJoin}
          isLoading={isRequesting}
          showWithdraw={false}
          isUserActive={isUserActive}
        />
      );
    }

    // User has a pending join request (waiting for admin)
    if (isAwaitingApproval) {
      return (
        <AwaitingApprovalButton
          onWithdraw={handleWithdrawRequest}
          isWithdrawing={isWithdrawing}
        />
      );
    }

    // User was invited to join
    if (wasInvited) {
      return (
        <InvitationButtons
          onAccept={() => setAgreementOpen(true)}
          onDecline={() => handleDecision("rejected")}
        />
      );
    }

    // User can request to join OR just sent a request
    if (canRequestToJoin || requestSent) {
      return (
        <RequestToJoinButton
          onClick={handleRequestToJoin}
          isLoading={isRequesting}
          showWithdraw={requestSent}
          onWithdraw={handleWithdrawRequest}
          isWithdrawing={isWithdrawing}
          isUserActive={isUserActive}
        />
      );
    }

    // Fallback: show status
    if (status) {
      return (
        <Button
          disabled
          sx={{
            ...buttonStyles.wide,
            ...buttonStyles.disabled,
            textTransform: "capitalize",
          }}
          variant="contained"
        >
          {status}
        </Button>
      );
    }

    return null;
  };

  return (
    <Box>
      {renderContent()}
      <GroupAgreementModal
        isOpen={agreementOpen}
        onAgree={() => {
          setAgreementOpen(false);
          handleDecision("accepted");
        }}
        onClose={() => setAgreementOpen(false)}
      />
    </Box>
  );
}

export default PendingAction;
